/* __next_internal_client_entry_do_not_use__ default auto */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// Global
import { Text, useComponentProps, useSitecore } from '@sitecore-content-sdk/nextjs';
import { tv } from 'tailwind-variants';
import { getFilteredBreadcrumbAncestors, shouldRenderBreadcrumb } from 'components/authorable/shared/site-structure/Breadcrumb/Breadcrumb.graphql';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import { createNavClickGTMEvent } from 'lib/utils/gtm-utils';
import { getTestProps } from 'lib/testing/utils';
import RemixIcon from 'helpers/RemixIcon/RemixIcon';
import { findComponent } from 'lib/utils/object-utils';
import { BrandAndThemeProvider } from 'lib/context/BrandAndThemeContext';
import { useIsMobile } from 'lib/hooks/useIsMobile';
import { editingHydrationProps } from 'lib/hooks/useEditingHydrationProps';
import useIsEditing from 'lib/hooks/useIsEditing';
/** Desktop: show all ancestors up to this count; beyond that, collapse to Home > … > last three. */ const MAX_DESKTOP_VISIBLE_ANCESTORS = 4;
const BreadcrumbClient = (props)=>{
    const isEditing = useIsEditing();
    const { page } = useSitecore();
    // In Sitecore Content SDK 2.0 (App Router), the SDK does not auto-inject the
    // `getComponentServerProps` result into the rendered component's props. Its
    // placeholder utility only forwards `{ fields, params, rendering }`. The
    // server-fetched data lives in `ComponentPropsContext` keyed by rendering
    // UID, so we pull it back out via `useComponentProps(rendering.uid)` here.
    // The shape is whatever `getComponentServerProps` returned (i.e.
    // `{ staticProps: BreadcrumbQueryResult }`).
    const ctxProps = useComponentProps(props?.rendering?.uid);
    const staticProps = props?.staticProps ?? ctxProps?.staticProps;
    const currentPage = staticProps?.currentPage;
    const { ancestors: rawAncestors, Title } = currentPage || {};
    const ancestors = rawAncestors ?? [];
    const { componentName, dataSource } = props?.rendering || {};
    const layoutSitecore = page.layout.sitecore;
    const isHeroContent = findComponent(layoutSitecore, 'Hero');
    const isExpertHeroContent = findComponent(layoutSitecore, 'ExpertHero');
    const theme = isHeroContent?.[0]?.params?.selectTheme || isExpertHeroContent?.[0]?.params?.selectTheme;
    const isMobile = useIsMobile();
    const filteredAncestors = getFilteredBreadcrumbAncestors(currentPage);
    /*
   * The full trail, current page included, sent with every breadcrumb click so a report can see
   * where in the hierarchy the user jumped back from — not just which crumb they hit.
   */ const breadcrumbPath = [
        ...filteredAncestors.map((ancestor)=>ancestor?.Title?.jsonValue?.value),
        Title?.jsonValue?.value
    ].filter(Boolean).join(' > ');
    /** Breadcrumb clicks are nav_click like the header's, distinguished by `nav_location`. */ const breadcrumbGtmEvent = (clickText, linkUrl)=>({
            ...createNavClickGTMEvent({
                clickText,
                navLocation: 'breadcrumbs',
                linkUrl,
                breadcrumbPath
            }),
            'gtm.element.dataset.gtmDatasourceId': dataSource,
            'gtm.element.dataset.gtmComponentName': componentName
        });
    if (!shouldRenderBreadcrumb(currentPage)) return /*#__PURE__*/ _jsx(_Fragment, {});
    const { base, linkWrapper, list, listItem, lastLevelText, icon } = TAILWIND_VARIANTS({
        theme: theme
    });
    const firstAncestor = filteredAncestors[0];
    const hasIntermediateParents = filteredAncestors.length > 1;
    const parentPage = hasIntermediateParents ? filteredAncestors[filteredAncestors.length - 1] : null;
    const shouldTruncateDesktop = !isMobile && filteredAncestors.length > MAX_DESKTOP_VISIBLE_ANCESTORS;
    const desktopTailStartIndex = filteredAncestors.length - (MAX_DESKTOP_VISIBLE_ANCESTORS - 1);
    const desktopEllipsisTarget = shouldTruncateDesktop ? filteredAncestors[desktopTailStartIndex - 1] : null;
    const desktopTailAncestors = shouldTruncateDesktop ? filteredAncestors.slice(-(MAX_DESKTOP_VISIBLE_ANCESTORS - 1)) : [];
    return /*#__PURE__*/ _jsx(BrandAndThemeProvider, {
        theme: theme,
        children: /*#__PURE__*/ _jsx("nav", {
            "aria-label": "Breadcrumb",
            className: base(),
            "data-component": "authorable/shared/site-structure/breadcrumb",
            ...editingHydrationProps(isEditing),
            ...getTestProps(`component-breadcrumb`),
            children: !isMobile ? /*#__PURE__*/ _jsxs("ul", {
                className: list(),
                children: [
                    shouldTruncateDesktop ? /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs("li", {
                                className: listItem(),
                                ...getTestProps(`parent-page-0`),
                                children: [
                                    /*#__PURE__*/ _jsx(LinkWrapper, {
                                        ctaVariant: "custom",
                                        className: linkWrapper(),
                                        field: {
                                            value: {
                                                href: filteredAncestors[0]?.url?.path,
                                                text: filteredAncestors[0]?.Title?.jsonValue?.value,
                                                title: filteredAncestors[0]?.Title?.jsonValue?.value
                                            }
                                        },
                                        gtmEvent: breadcrumbGtmEvent(filteredAncestors[0]?.Title?.jsonValue?.value, filteredAncestors[0]?.url?.path),
                                        ...getTestProps(`link-0`)
                                    }),
                                    /*#__PURE__*/ _jsx(RemixIcon, {
                                        className: icon(),
                                        name: "arrow-right-line",
                                        size: "md"
                                    })
                                ]
                            }, filteredAncestors[0]?.url?.path),
                            desktopEllipsisTarget && /*#__PURE__*/ _jsxs("li", {
                                className: listItem(),
                                ...getTestProps(`parent-page-ellipsis`),
                                children: [
                                    /*#__PURE__*/ _jsx(LinkWrapper, {
                                        ctaVariant: "custom",
                                        className: linkWrapper(),
                                        field: {
                                            value: {
                                                href: desktopEllipsisTarget.url?.path,
                                                text: '...',
                                                title: desktopEllipsisTarget.Title?.jsonValue?.value ?? 'Parent page'
                                            }
                                        },
                                        gtmEvent: breadcrumbGtmEvent(desktopEllipsisTarget.Title?.jsonValue?.value, desktopEllipsisTarget.url?.path),
                                        ...getTestProps(`link-ellipsis`)
                                    }),
                                    /*#__PURE__*/ _jsx(RemixIcon, {
                                        className: icon(),
                                        name: "arrow-right-line",
                                        size: "md"
                                    })
                                ]
                            }, `ellipsis-${desktopEllipsisTarget.url?.path}`),
                            desktopTailAncestors.map((ancestor, index)=>{
                                const { url, Title } = ancestor || {};
                                const listIndex = index + 2;
                                return /*#__PURE__*/ _jsxs("li", {
                                    className: listItem(),
                                    ...getTestProps(`parent-page-${listIndex}`),
                                    children: [
                                        /*#__PURE__*/ _jsx(LinkWrapper, {
                                            ctaVariant: "custom",
                                            className: linkWrapper(),
                                            field: {
                                                value: {
                                                    href: url?.path,
                                                    text: Title?.jsonValue?.value,
                                                    title: Title?.jsonValue?.value
                                                }
                                            },
                                            gtmEvent: breadcrumbGtmEvent(Title?.jsonValue?.value, url?.path),
                                            ...getTestProps(`link-${listIndex}`)
                                        }),
                                        /*#__PURE__*/ _jsx(RemixIcon, {
                                            className: icon(),
                                            name: "arrow-right-line",
                                            size: "md"
                                        })
                                    ]
                                }, url?.path);
                            })
                        ]
                    }) : filteredAncestors.map((ancestor, index)=>{
                        const { url, Title } = ancestor || {};
                        return /*#__PURE__*/ _jsxs("li", {
                            className: listItem(),
                            ...getTestProps(`parent-page-${index}`),
                            children: [
                                /*#__PURE__*/ _jsx(LinkWrapper, {
                                    ctaVariant: "custom",
                                    className: linkWrapper(),
                                    field: {
                                        value: {
                                            href: url?.path,
                                            text: Title?.jsonValue?.value,
                                            title: Title?.jsonValue?.value
                                        }
                                    },
                                    gtmEvent: breadcrumbGtmEvent(Title?.jsonValue?.value, url?.path),
                                    ...getTestProps(`link-${index}`)
                                }),
                                /*#__PURE__*/ _jsx(RemixIcon, {
                                    className: icon(),
                                    name: "arrow-right-line",
                                    size: "md"
                                })
                            ]
                        }, url?.path);
                    }),
                    ancestors?.length > 0 && /*#__PURE__*/ _jsx("li", {
                        "aria-current": "true",
                        ...getTestProps(`current-page`),
                        children: /*#__PURE__*/ _jsx(Text, {
                            className: lastLevelText(),
                            encode: false,
                            field: {
                                value: Title?.jsonValue?.value
                            },
                            tag: "span",
                            ...getTestProps(`current-page-text`)
                        })
                    })
                ]
            }) : /*#__PURE__*/ _jsxs("ul", {
                className: list(),
                children: [
                    firstAncestor && /*#__PURE__*/ _jsxs(_Fragment, {
                        children: [
                            /*#__PURE__*/ _jsxs("li", {
                                className: listItem(),
                                ...getTestProps('parent-page-0'),
                                children: [
                                    /*#__PURE__*/ _jsx(LinkWrapper, {
                                        ctaVariant: "custom",
                                        className: linkWrapper(),
                                        field: {
                                            value: {
                                                href: firstAncestor?.url?.path,
                                                text: firstAncestor?.Title?.jsonValue?.value,
                                                title: firstAncestor?.Title?.jsonValue?.value
                                            }
                                        },
                                        gtmEvent: breadcrumbGtmEvent(firstAncestor?.Title?.jsonValue?.value, firstAncestor?.url?.path),
                                        ...getTestProps('link-0')
                                    }),
                                    /*#__PURE__*/ _jsx(RemixIcon, {
                                        className: icon(),
                                        name: "arrow-right-line",
                                        size: "md"
                                    })
                                ]
                            }),
                            hasIntermediateParents && parentPage && /*#__PURE__*/ _jsx(_Fragment, {
                                children: /*#__PURE__*/ _jsxs("li", {
                                    className: listItem(),
                                    children: [
                                        /*#__PURE__*/ _jsx(LinkWrapper, {
                                            ctaVariant: "custom",
                                            className: linkWrapper(),
                                            field: {
                                                value: {
                                                    href: parentPage?.url?.path,
                                                    text: '...',
                                                    title: parentPage?.Title?.jsonValue?.value ?? 'Parent page'
                                                }
                                            },
                                            gtmEvent: breadcrumbGtmEvent(parentPage?.Title?.jsonValue?.value, parentPage?.url?.path)
                                        }),
                                        /*#__PURE__*/ _jsx(RemixIcon, {
                                            className: icon(),
                                            name: "arrow-right-line",
                                            size: "md"
                                        })
                                    ]
                                })
                            })
                        ]
                    }),
                    ancestors?.length > 0 && /*#__PURE__*/ _jsx("li", {
                        "aria-current": "true",
                        ...getTestProps(`current-page`),
                        children: /*#__PURE__*/ _jsx(Text, {
                            className: lastLevelText(),
                            encode: false,
                            field: {
                                value: Title?.jsonValue?.value
                            },
                            tag: "span",
                            ...getTestProps(`current-page-text`)
                        })
                    })
                ]
            })
        })
    });
};
export default BreadcrumbClient;
const TAILWIND_VARIANTS = tv({
    defaultVariants: {
        theme: 'ThemesWhite'
    },
    variants: {
        theme: {
            ThemesWhite: {},
            ThemesLight: {},
            ThemesDark: {
                lastLevelText: [
                    'text-white'
                ],
                linkWrapper: [
                    'hover:text-white',
                    '!text-color-core-color-4-SkyBlue'
                ]
            }
        }
    },
    slots: {
        base: [
            'component',
            'text-typography-font-size-body-medium',
            'pt-[20px]',
            'pb-spacing-spacing-16',
            'bg-component-hero-bg',
            'relative',
            'before:z-[-1]',
            'before:h-full',
            'before:absolute',
            'before:top-0',
            'before:left-[calc(-50vw+50%)]',
            'before:bg-component-hero-bg',
            'before:right-[calc(-50vw+50%)]'
        ],
        list: [
            'flex',
            'flex-wrap',
            'gap-spacing-spacing-8',
            'items-center',
            'list'
        ],
        listItem: [
            'flex',
            'items-center',
            'list-none',
            'gap-spacing-spacing-8'
        ],
        linkWrapper: [
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'font-typography-font-family-body',
            'font-regular',
            'text-color-core-color-4-skyblue-darkTint',
            'hover:text-color-text-text'
        ],
        lastLevelText: [
            'text-typography-font-size-body-medium',
            'leading-typography-line-height-body-medium',
            'font-typography-font-family-body',
            'font-semibold',
            'text-color-grayscale-black'
        ],
        icon: [
            'text-color-core-color-4-skyblue-darkTint'
        ]
    }
});
